// Single owner of the forced Auth0 access-token refresh used for post-login
// provisioning (FORT-293).
//
// Several independent parts of the SPA detect a stale / pre-provision token on
// first paint and each force `getAccessTokenSilently({ cacheMode: 'off' })`:
//   - ClientProvider.syncAndRefreshClients   (pending-slug signup)
//   - MemberContext.syncAuth                  (requiresTokenRefresh)
//   - ClientConfigProvider                    (tenancy-gate stale-token recovery + register)
//   - MemberProfileScreenV2.handleCreateMember(self-service create)
//   - NewMemberRegistration                   (staff new-member create)
//
// These run concurrently, so without coordination the same first paint can fire
// three or more network token refreshes that race each other. This coordinator
// collapses *concurrent* callers onto one in-flight refresh: while a refresh is
// running, every caller receives the same promise; once it settles the in-flight
// slot is cleared so the NEXT caller still gets a genuinely fresh token (it may
// need Auth0 app_metadata written after the earlier refresh began — sequential
// calls must NOT be coalesced, only concurrent ones).
//
// This is deliberately framework-agnostic (no React) so it can be unit-tested in
// isolation and shared by every caller.

type GetAccessTokenSilently = (
    options?: { cacheMode?: 'on' | 'off' | 'cache-only' },
) => Promise<string>;

let inFlight: Promise<string> | null = null;

export interface ForceTokenRefreshOptions {
    /**
     * Bypass the in-flight slot and mint a genuinely fresh token even if a
     * refresh is already running. Use this for a *causal* refresh — one that must
     * observe an Auth0 write the CALLER just made (e.g. after member creation
     * writes `membership_id`, or self-registration writes the `clients` claim).
     *
     * Plain coalescing is temporal only: a caller that writes-then-refreshes could
     * otherwise coalesce onto a refresh that started BEFORE its write landed and
     * be handed a token that predates its own write, 403-ing the next call. First-
     * paint provisioning refreshes (MemberContext / ClientProvider `/auth/sync`,
     * the tenancy-gate recovery) are convergent/idempotent and safe to coalesce,
     * so they leave this false. A bypass refresh still becomes the new in-flight
     * slot, so later concurrent callers coalesce onto the fresher token.
     */
    bypassInFlight?: boolean;
}

/**
 * Force a fresh Auth0 access token (`cacheMode: 'off'`), coalescing concurrent
 * callers onto a single network refresh. Returns the shared in-flight promise
 * while a refresh is running; starts a new one otherwise (always, when
 * `bypassInFlight` is set — see {@link ForceTokenRefreshOptions}).
 *
 * Rejections propagate to every awaiting caller and clear the in-flight slot, so
 * a later call retries rather than re-throwing a stale error.
 */
export function forceTokenRefresh(
    getAccessTokenSilently: GetAccessTokenSilently,
    options?: ForceTokenRefreshOptions,
): Promise<string> {
    if (inFlight && !options?.bypassInFlight) return inFlight;

    const pending = getAccessTokenSilently({ cacheMode: 'off' });
    inFlight = pending;
    // Clear the slot once settled, on BOTH success and failure (guarding against
    // a slot a concurrent reset already moved on from). Using then(clear, clear)
    // rather than finally() so this internal cleanup chain also *handles* a
    // rejection — otherwise it would surface as an unhandled promise rejection.
    // Callers still receive `pending` and handle its rejection themselves.
    const clear = () => {
        if (inFlight === pending) inFlight = null;
    };
    pending.then(clear, clear);
    return pending;
}

/**
 * Drop any tracked in-flight refresh. Call on logout so a session boundary can't
 * hand the next user a promise from the previous session; also used to isolate
 * unit tests that share this module singleton.
 */
export function resetTokenRefreshCoordinator(): void {
    inFlight = null;
}
